import * as React from 'react';
import { FC, ReactNode } from 'react';
import classnames from 'classnames';
import numeral from 'numeral';

import { moneyStr, moneyAccurate as money } from 'app/helpers/money';
import Expander from './Expander';
import { useContext, Locales, Fees } from './useFormContext';

const ConversionFee: FC = () => {
  const { locales, dynamicConversionFee } = useContext();

  if (dynamicConversionFee <= 0) return null;

  return (
    <div className="rs-flex rs-gap_small__zero">
      <div className="rs-flex-item rs-medium-8">{locales.conversion_fee}</div>
      <div className="rs-flex-item rs-medium-4 rs-text-align_right">{dynamicConversionFee * 100}%</div>
    </div>
  );
};

const operationFee = (fees: Fees, creditedCurrency: string, locales: Locales) => {
  const opFee = fees.operation > 0 ? `${numeral(fees.operation * 100).format('0.00')}%` : '';
  const minFee = fees.min > 0 ? ` (${locales.min_transaction}\u00A0${moneyStr(fees.min, creditedCurrency)})` : '';
  return opFee + minFee;
};

const fixedFee = (fees: Fees, vendorCurrency: string) => (fees.fixed > 0 ? moneyStr(fees.fixed, vendorCurrency) : '');

const OtherFees: FC<{ vendorCurrency: string; creditedCurrency: string }> = ({ vendorCurrency, creditedCurrency }) => {
  const { fees, locales } = useContext();

  if (fees.operation === 0 && fees.min === 0 && fees.fixed === 0) return null;

  const allFees = [operationFee(fees, creditedCurrency, locales), fixedFee(fees, vendorCurrency)]
    .filter((v) => v.length > 0)
    .join(' + ');

  const labelId = 'other-fees-label';

  return (
    <div className="rs-flex rs-gap_small__zero">
      <div id={labelId} className="rs-flex-item rs-medium-7">
        {locales.operation_fee}
      </div>
      <div aria-labelledby={labelId} className="rs-flex-item rs-medium-5 rs-text-align_right">
        {allFees}
      </div>
    </div>
  );
};

const ExchangeRate: FC<{ formCurrency: string; creditedCurrency: string }> = ({ formCurrency, creditedCurrency }) => {
  if (formCurrency === creditedCurrency) return null;

  const { locales, rate } = useContext();
  const labelId = 'exchange-rate-label';

  return (
    <div className="rs-flex rs-gap_small__zero">
      <div
        id={labelId}
        className="rs-flex-item rs-medium-7"
      >{`${locales.exchange_rate} ${formCurrency} / ${creditedCurrency}`}</div>
      <div aria-labelledby={labelId} className="rs-flex-item rs-medium-5 rs-text-align_right">
        {rate}
      </div>
    </div>
  );
};

const ResultWithFees: FC<{
  formAmount: string;
  formCurrency: string;
  resultSum: number;
  creditedCurrency: string;
  exchangeRate: ReactNode;
  conversionFee: ReactNode;
  otherFees: ReactNode;
}> = ({ formAmount, formCurrency, resultSum, creditedCurrency, exchangeRate, conversionFee, otherFees }) => {
  if (!exchangeRate && !conversionFee && !otherFees) return null;

  const { isDeposit, locales } = useContext();

  return (
    <Expander text={locales.details} className={classnames({ zero: resultSum === 0 }, 'result-calculation')}>
      <div className="rs-flex origin-amount rs-gap_small__zero">
        <div className="rs-flex-item rs-medium-8">{isDeposit ? locales.deposit_amount : locales.withdrawal_amount}</div>
        <div className="rs-flex-item rs-medium-4 rs-text-align_right">{money(Number(formAmount), formCurrency)}</div>
      </div>
      {exchangeRate}
      {conversionFee}
      {otherFees}
      <hr />
      <div className="rs-flex rs-gap_small__zero total">
        <div className="rs-flex-item rs-medium-8">{locales.total}</div>
        <div className="rs-flex-item rs-medium-4 rs-text-align_right">{money(resultSum, creditedCurrency)}</div>
      </div>
    </Expander>
  );
};

const ResultCalculation: FC<{
  formAmount: string;
  formCurrency: string;
  resultSum: number;
  creditedCurrency: string;
  vendorCurrency: string;
}> = ({ formAmount, formCurrency, resultSum, creditedCurrency, vendorCurrency }) => (
  <ResultWithFees
    formAmount={formAmount}
    formCurrency={formCurrency}
    resultSum={resultSum}
    creditedCurrency={creditedCurrency}
    exchangeRate={<ExchangeRate formCurrency={formCurrency} creditedCurrency={creditedCurrency} />}
    conversionFee={<ConversionFee />}
    otherFees={<OtherFees vendorCurrency={vendorCurrency} creditedCurrency={creditedCurrency} />}
  />
);

export default ResultCalculation;
